import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import InvDataGrid from '../../components/InvDataGrid';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { InvTextField, InvSelect } from '../../components/InvField';
import MaterialMasterShell from '../../components/master/MaterialMasterShell';
import MasterListShell from '../../components/master/MasterListShell';
import FilterToolbar from '../../components/FilterToolbar';
import MasterFormDialog from '../../components/master/MasterFormDialog';
import ActiveStatusBadge from '../../components/master/ActiveStatusBadge';
import {
  getTaxMasters, createTaxMaster, updateTaxMaster, deleteTaxMaster,
} from '../../api/taxMaster';
import {
  TAX_MASTER_PAGE_SIZE, GST_RATE_OPTIONS,
  // TAX_CATEGORY_OPTIONS, // hidden until backend returns tax_category
  normalizeGstRateForSelect, STATUS_FILTER_OPTIONS,
} from '../../constants';
import { normalizeTaxMaster, taxMasterRowId, toInventoryPage } from '../../utils/masterEntity';
import { formatDate } from '../../utils';
import { IPD_COLORS, cardSx, switchSx } from '../../theme';
import useMasterList from '../../hooks/useMasterList';

const EMPTY_FORM = {
  hsn_code: '',
  gst_rate: '',
  description: '',
  // tax_category: '', // hidden until backend returns tax_category
};

const EDIT_FORM = {
  gst_rate: '',
  description: '',
  is_active: true,
};

// const CATEGORY_FILTER_OPTIONS = [
//   { value: '', label: 'All Categories' },
//   ...TAX_CATEGORY_OPTIONS,
// ];

function buildListParams({ page, search, statusFilter, pageSize }) {
  const params = { limit: pageSize, offset: page * pageSize };
  if (search.trim()) params.search = search.trim();
  // if (categoryFilter) params.tax_category = categoryFilter; // hidden until backend returns tax_category
  if (statusFilter === 'active') params.is_active = true;
  if (statusFilter === 'inactive') params.is_active = false;
  return params;
}

function buildCreateBody(data) {
  return {
    hsn_code: data.hsn_code.trim(),
    gst_rate: Number(data.gst_rate),
    description: data.description.trim(),
    // tax_category: data.tax_category, // hidden until backend returns tax_category
    is_active: true,
  };
}

function buildUpdateBody(data) {
  return {
    gst_rate: Number(data.gst_rate),
    description: data.description?.trim() || '',
    is_active: data.is_active !== false,
  };
}

function mapRowToEditForm(row) {
  return {
    gst_rate: normalizeGstRateForSelect(row?.gst_rate),
    description: row?.description ?? '',
    is_active: row?.is_active !== false,
  };
}

export default function TaxMasterList() {
  const list = useMasterList({ pageSize: TAX_MASTER_PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput,
    search, statusFilter, setStatusFilter,
    resetFilters, pageSize,
  } = list;

  // const [categoryFilter, setCategoryFilter] = useState(''); // hidden until backend returns tax_category
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: EMPTY_FORM });
  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm({ defaultValues: EDIT_FORM });

  const hasActiveFilters = Boolean(search.trim() || statusFilter);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getTaxMasters(buildListParams({ page, search, statusFilter, pageSize }));
      const { items: fetched, total } = toInventoryPage(raw, { page, pageSize });
      setItems(fetched.map(normalizeTaxMaster));
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, pageSize, setLoading, setError, setItems, setTotalCount]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editTarget) resetEdit(mapRowToEditForm(editTarget));
  }, [editTarget, resetEdit]);

  function openAddModal() {
    reset(EMPTY_FORM);
    setAddOpen(true);
  }

  function closeAddModal() {
    if (saving) return;
    setAddOpen(false);
    reset(EMPTY_FORM);
  }

  function closeEditModal() {
    setEditTarget(null);
    resetEdit(EDIT_FORM);
  }

  function handleResetFilters() {
    resetFilters();
    // setCategoryFilter(''); // hidden until backend returns tax_category
  }

  async function onSubmit(data) {
    setSaving(true);
    try {
      await createTaxMaster(buildCreateBody(data));
      closeAddModal();
      toast.success('HSN/GST entry created');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to create HSN/GST entry');
    } finally {
      setSaving(false);
    }
  }

  async function onEditSubmit(data) {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await updateTaxMaster(taxMasterRowId(editTarget), buildUpdateBody(data));
      closeEditModal();
      toast.success('HSN/GST entry updated');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to update HSN/GST entry');
    } finally {
      setEditSaving(false);
    }
  }

  async function confirmDeactivate() {
    setDeactivating(true);
    try {
      await deleteTaxMaster(taxMasterRowId(deactivateTarget));
      setDeactivateTarget(null);
      toast.success('HSN/GST entry deactivated');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to deactivate HSN/GST entry');
      setDeactivateTarget(null);
    } finally {
      setDeactivating(false);
    }
  }

  const columns = useMemo(() => [
    { field: 'hsn_code', headerName: 'HSN Code', width: 130 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 180 },
    {
      field: 'gst_rate',
      headerName: 'GST Rate',
      width: 100,
      valueFormatter: (p) => (p.value != null && p.value !== '' ? `${p.value}%` : '—'),
    },
    // { field: 'tax_category', headerName: 'Tax Category', width: 140, valueGetter: (p) => p.row.tax_category || '—' },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (p) => <ActiveStatusBadge active={p.value} />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit entry">
            <IconButton size="small" onClick={() => setEditTarget(p.row)} sx={{ color: IPD_COLORS.primary }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.is_active !== false && (
            <Tooltip title="Deactivate entry">
              <IconButton size="small" onClick={() => setDeactivateTarget(p.row)} sx={{ color: IPD_COLORS.error.text }}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], []);

  const listSubtitle = hasActiveFilters
    ? 'Showing filtered results — adjust or reset filters to see all entries.'
    : 'Manage HSN codes and their GST rates. Items and transactions derive GST from here.';

  return (
    <MaterialMasterShell
      title="Tax Master"
      description="Central HSN → GST rate lookup used by items, purchase orders, GRNs, and pharmacy billing."
      badge={`${totalCount} ${totalCount === 1 ? 'entry' : 'entries'}`}
      actions={
        <InvButton startIcon={<AddIcon />} onClick={openAddModal}>
          Add HSN/GST
        </InvButton>
      }
    >
      <MasterListShell
        title="All HSN/GST Entries"
        subtitle={listSubtitle}
        filters={(
          <>
            <FilterToolbar.Search
              placeholder="Search by HSN code or description..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {/* Category filter hidden until backend returns tax_category
            <FilterToolbar.Filter
              label="Category"
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
              options={CATEGORY_FILTER_OPTIONS}
            />
            */}
            <FilterToolbar.Filter
              label="Status"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              options={STATUS_FILTER_OPTIONS}
            />
            <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
          </>
        )}
      >
        <InvDataGrid
          embedded
          fillHeight
          rows={items}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={load}
          getRowId={taxMasterRowId}
          emptyMessage="No HSN/GST entries yet"
          emptyDescription="Add your first HSN code and GST rate. Items will use this to auto-fill tax on save."
          emptyAction={<InvButton startIcon={<AddIcon />} onClick={openAddModal}>Add HSN/GST</InvButton>}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          pageSizeOptions={[pageSize]}
        />
      </MasterListShell>

      <MasterFormDialog
        open={addOpen}
        title="Add HSN/GST Entry"
        subtitle="Set the GST rate that will auto-apply wherever this HSN is selected."
        formId="add-tax-master-form"
        onSubmit={handleSubmit(onSubmit)}
        onClose={closeAddModal}
        saving={saving}
        submitLabel="Add Entry"
        submitIcon={<AddIcon />}
      >
        <Controller
          name="hsn_code"
          control={control}
          rules={{ required: 'HSN code is required' }}
          render={({ field }) => (
            <InvTextField
              {...field}
              label="HSN Code *"
              placeholder="e.g. 30049099"
              error={errors.hsn_code}
              autoFocus
              inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            />
          )}
        />
        <Controller
          name="gst_rate"
          control={control}
          rules={{ required: 'GST rate is required' }}
          render={({ field }) => (
            <InvSelect
              label="GST Rate *"
              value={field.value}
              onChange={e => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              options={GST_RATE_OPTIONS}
              error={errors.gst_rate}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          rules={{ required: 'Description is required' }}
          render={({ field }) => (
            <InvTextField {...field} label="Description *" placeholder="e.g. Medicaments" error={errors.description} />
          )}
        />
        {/* Tax Category hidden until backend returns tax_category
        <Controller
          name="tax_category"
          control={control}
          rules={{ required: 'Tax category is required' }}
          render={({ field }) => (
            <InvSelect
              label="Tax Category *"
              value={field.value}
              onChange={field.onChange}
              options={TAX_CATEGORY_OPTIONS}
              error={errors.tax_category}
            />
          )}
        />
        */}
      </MasterFormDialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={editSaving ? undefined : closeEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: cardSx }}
      >
        <DialogTitle sx={{
          color: IPD_COLORS.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}>
          Edit HSN/GST Entry
          <IconButton onClick={closeEditModal} disabled={editSaving} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            id="edit-tax-master-form"
            onSubmit={handleEditSubmit(onEditSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <InvTextField label="HSN Code" value={editTarget?.hsn_code || ''} disabled />
            {/* <InvTextField label="Tax Category" value={editTarget?.tax_category || '—'} disabled /> */}
            <Controller
              name="gst_rate"
              control={editControl}
              rules={{ required: 'GST rate is required' }}
              render={({ field }) => (
                <InvSelect
                  label="GST Rate *"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                  options={GST_RATE_OPTIONS}
                  error={editErrors.gst_rate}
                />
              )}
            />
            <Controller
              name="description"
              control={editControl}
              rules={{ required: 'Description is required' }}
              render={({ field }) => (
                <InvTextField {...field} label="Description *" error={editErrors.description} />
              )}
            />
            <Controller
              name="is_active"
              control={editControl}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} sx={switchSx} />
                  }
                  label="Active"
                  sx={{ color: IPD_COLORS.textPrimary, m: 0 }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={closeEditModal} disabled={editSaving}>Cancel</InvButton>
          <InvButton type="submit" form="edit-tax-master-form" loading={editSaving}>Save Changes</InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        title="Deactivate HSN/GST Entry"
        description={`Deactivate HSN "${deactivateTarget?.hsn_code}"? It will no longer be selectable for new items.`}
        destructive
        loading={deactivating}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </MaterialMasterShell>
  );
}
